import React from 'react';
import { motion } from 'framer-motion';
import { Navigation, Clock, Waves, Anchor, Compass, Fish } from 'lucide-react';

interface Destino {
    icon: React.ReactNode;
    name: string;
    description: string;
    distance: string;
    time: string;
    difficulty: 'Fácil' | 'Moderado' | 'Avançado';
}

const destinos: Destino[] = [
    {
        icon: <Waves className="w-6 h-6" />,
        name: 'Ilha das Cabras',
        description: 'Parada obrigatória para mergulho e snorkel. Águas cristalinas e vida marinha abundante a poucos minutos da costa.',
        distance: '1.5 km',
        time: '~10 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Anchor className="w-6 h-6" />,
        name: 'Praia de Laranjeiras',
        description: 'Uma das praias mais bonitas da região. Acesso exclusivo por barco ou trilha, com restaurantes à beira-mar.',
        distance: '3 km',
        time: '~15 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Compass className="w-6 h-6" />,
        name: 'Ilha do Arvoredo',
        description: 'Reserva biológica com mergulho de nível internacional. Águas azuis, corais e naufrágios para explorar.',
        distance: '11 km',
        time: '~40 min',
        difficulty: 'Avançado',
    },
    {
        icon: <Navigation className="w-6 h-6" />,
        name: 'Praia do Estaleiro',
        description: 'Enseada paradisíaca entre costões rochosos. Ideal para ancorar e curtir um dia tranquilo com a família.',
        distance: '4 km',
        time: '~20 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Fish className="w-6 h-6" />,
        name: 'Porto Belo',
        description: 'Baía abrigada perfeita para navegação segura. Centro histórico charmoso e gastronomia de frutos do mar.',
        distance: '8 km',
        time: '~30 min',
        difficulty: 'Moderado',
    },
    {
        icon: <Waves className="w-6 h-6" />,
        name: 'Bombinhas',
        description: 'Capital do mergulho em SC. Praias de água transparente, trilhas e a famosa Praia da Sepultura.',
        distance: '15 km',
        time: '~45 min',
        difficulty: 'Moderado',
    },
];

const difficultyColors: Record<string, string> = {
    'Fácil': 'bg-green-100 text-green-700',
    'Moderado': 'bg-amber-100 text-amber-700',
    'Avançado': 'bg-sky-100 text-sky-700',
};

const DestinosSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-background relative overflow-hidden">
        {/* Gold orb */}
        <div
            className="absolute left-0 top-1/4 w-[400px] h-[400px] pointer-events-none"
            style={{
                background: 'radial-gradient(circle, hsl(200 70% 50% / 0.05), transparent 65%)',
                filter: 'blur(40px)',
            }}
        />

        <div className="container mx-auto px-6 relative z-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Destinos
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                    Destinos Imperdíveis Partindo de BC
                </h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                    Da Ilha das Cabras a Bombinhas — as melhores rotas estão ao alcance de quem navega de Balneário Camboriú.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {destinos.map((dest, i) => (
                    <motion.div
                        key={dest.name}
                        initial={{ opacity: 0, y: 25 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        whileHover={{ scale: 1.03, y: -5 }}
                        className="group bg-white border border-border rounded-xl p-6 cursor-default transition-all duration-300 hover:shadow-xl hover:border-gold/30 relative overflow-hidden"
                    >
                        {/* Subtle gold corner glow on hover */}
                        <div
                            className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                                background: 'radial-gradient(circle, hsl(43 74% 53% / 0.15), transparent 70%)',
                            }}
                        />

                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/5 group-hover:bg-gold/10 flex items-center justify-center text-primary group-hover:text-gold transition-colors duration-300">
                                    {dest.icon}
                                </div>
                                <h4 className="font-bold text-lg text-primary">{dest.name}</h4>
                            </div>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${difficultyColors[dest.difficulty]}`}>
                                {dest.difficulty}
                            </span>
                        </div>

                        {/* Description */}
                        <p className="text-muted-foreground text-sm leading-relaxed mb-5">{dest.description}</p>

                        {/* Footer stats */}
                        <div className="flex items-center gap-6 pt-4 border-t border-border/60">
                            <div className="flex items-center gap-1.5 text-sm">
                                <Navigation className="w-3.5 h-3.5 text-gold" />
                                <span className="font-semibold text-primary">{dest.distance}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm">
                                <Clock className="w-3.5 h-3.5 text-gold" />
                                <span className="font-semibold text-primary">{dest.time}</span>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

export default DestinosSection;
